import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { interestRepository } from '@/data/interests/repositories/interest_repository_impl'
import { CreateInterestUseCase } from '@/domain/interests/usecases/create_interest.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { InterestFormErrors } from '../models/interests_ui_state'

const defaultCreateInterest = new CreateInterestUseCase(interestRepository)

export interface UseAddInterestViewModelDeps {
  createInterest?: CreateInterestUseCase
}

export function useAddInterestViewModel(deps: UseAddInterestViewModelDeps = {}) {
  const navigate = useNavigate()
  const createInterest = deps.createInterest ?? defaultCreateInterest
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [interestName, setInterestName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [fileName, setFileName] = useState('No file chosen')
  const [errors, setErrors] = useState<InterestFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFileChange = useCallback((file: File | undefined) => {
    if (file) {
      setSelectedFile(file)
      setFileName(file.name)
      setErrors(prev => ({ ...prev, icon: undefined }))
    } else {
      setSelectedFile(null)
      setFileName('No file chosen')
    }
  }, [])

  const submit = useCallback(async () => {
    setIsSubmitting(true)
    const newErrors: InterestFormErrors = {}

    if (!interestName.trim()) {
      newErrors.name = 'Interest name is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      return
    }

    try {
      if (selectedFile) {
        alert(
          'Image upload requires a hosted iconUrl. Enter a URL or upload via your CDN, then save.'
        )
        setIsSubmitting(false)
        return
      }

      await createInterest.execute({ name: interestName.trim() })
      navigate('/admin/interest')
    } catch (error: unknown) {
      logError('Failed to create interest:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to create interest. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }, [createInterest, interestName, navigate, selectedFile])

  const cancel = useCallback(() => {
    navigate('/admin/interest')
  }, [navigate])

  return {
    fileInputRef,
    form: {
      interestName,
      selectedFile,
      isActive,
      fileName,
      errors,
      isSubmitting,
    },
    actions: {
      setInterestName,
      setIsActive,
      handleFileChange,
      openFilePicker: () => fileInputRef.current?.click(),
      submit,
      cancel,
      clearFieldError: (field: keyof InterestFormErrors) =>
        setErrors(prev => ({ ...prev, [field]: undefined })),
    },
  }
}
